/**
 * SkillDelivery Platform
 *
 * Copyright © 2026 SKD RATWANI Technology & Software Solutions Pvt Ltd
 * All Rights Reserved.
 *
 * PROPRIETARY AND CONFIDENTIAL
 *
 * This source code is the exclusive property of SKD RATWANI Technology &
 * Software Solutions Pvt Ltd, operating as "SkillDelivery". Unauthorized
 * copying, modification, distribution, or use of this software, via any
 * medium, is strictly prohibited without express written permission.
 *
 * For licensing inquiries: legal@skilldelivery.co.in
 *
 * Registered Office: Sitapur, Uttar Pradesh, India - 261001
 * Jurisdiction: Noida, Uttar Pradesh, India - 201301
 */

import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAdminAuth } from '../../contexts/AdminAuthContext';
import adminApi from '../../services/adminApi';
import AdminLayout from '../../components/AdminLayout';
import './TrainingJobs.css';

const TrainingJobs = () => {
  const navigate = useNavigate();
  const { hasPermission } = useAdminAuth();

  // State
  const [jobs, setJobs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [filters, setFilters] = useState({
    domain: '',
    status: '',
    page: 1,
    limit: 20
  });
  const [pagination, setPagination] = useState({
    total: 0,
    pages: 0,
    currentPage: 1
  });

  /**
   * Load jobs
   */
  useEffect(() => {
    loadJobs();
  }, [filters]);

  const loadJobs = async () => {
    try {
      setLoading(true);
      setError('');

      // adminApi.getTrainingJobs already returns response.data
      // So 'data' here is directly the backend response: { success, count, jobs }
      const data = await adminApi.getTrainingJobs(filters);
      console.log('[TrainingJobs] Backend response:', JSON.stringify(data, null, 2));

      if (!data || !data.jobs) {
        console.error('[TrainingJobs] Invalid response structure:', JSON.stringify(data, null, 2));
        throw new Error('Invalid response from server. Expected { success, count, jobs }');
      }

      console.log('[TrainingJobs] Jobs count:', data.count);
      console.log('[TrainingJobs] Jobs:', data.jobs);

      setJobs(data.jobs || []);
      setPagination({
        total: data.count || 0,
        pages: Math.ceil((data.count || 0) / filters.limit),
        currentPage: filters.page || 1
      });

    } catch (err) {
      console.error('Load jobs error:', err);
      setError(err.response?.data?.message || err.message || 'Failed to load training jobs');
    } finally {
      setLoading(false);
    }
  };

  /**
   * Handle filter change
   */
  const handleFilterChange = (key, value) => {
    setFilters(prev => ({
      ...prev,
      [key]: value,
      page: 1 // Reset to first page on filter change
    }));
  };

  /**
   * Handle page change
   */
  const handlePageChange = (newPage) => {
    setFilters(prev => ({ ...prev, page: newPage }));
  };

  /**
   * Handle retry job
   */
  const handleRetry = async (jobId) => {
    if (!window.confirm('Retry this training job?')) return;

    try {
      await adminApi.retryTrainingJob(jobId);
      loadJobs(); // Reload jobs
    } catch (err) {
      alert(err.response?.data?.message || 'Failed to retry job');
    }
  };

  /**
   * Handle cancel job
   */
  const handleCancel = async (jobId) => {
    if (!window.confirm('Cancel this training job? This cannot be undone.')) return;

    try {
      await adminApi.cancelTrainingJob(jobId);
      loadJobs(); // Reload jobs
    } catch (err) {
      alert(err.response?.data?.message || 'Failed to cancel job');
    }
  };

  /**
   * Get status class
   */
  const getStatusClass = (status) => {
    switch (status?.toLowerCase()) {
      case 'completed':
      case 'success':
        return 'status-success';
      case 'running':
      case 'in_progress':
        return 'status-running';
      case 'failed':
      case 'error':
        return 'status-failed';
      case 'pending':
      case 'queued':
        return 'status-pending';
      case 'cancelled':
        return 'status-cancelled';
      default:
        return 'status-unknown';
    }
  };

  /**
   * Format date
   */
  const formatDate = (dateString) => {
    if (!dateString) return '--';
    const date = new Date(dateString);
    return date.toLocaleString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  /**
   * Format duration
   */
  const formatDuration = (job) => {
    if (!job.startedAt) return '--';

    const start = new Date(job.startedAt);
    const end = job.completedAt ? new Date(job.completedAt) : new Date();
    const durationMs = end - start;

    const hours = Math.floor(durationMs / (1000 * 60 * 60));
    const minutes = Math.floor((durationMs % (1000 * 60 * 60)) / (1000 * 60));

    if (hours > 0) {
      return `${hours}h ${minutes}m`;
    }
    return `${minutes}m`;
  };

  return (
    <AdminLayout showBackButton={true} backPath="/admin/dashboard">
      <div className="training-jobs">
        {/* Header */}
        <div className="page-header">
          <div className="header-left">
            <div>
              <h1>Training Jobs</h1>
              <p className="page-subtitle">Manage model training and fine-tuning jobs</p>
            </div>
          </div>

          {hasPermission('training.create') && (
            <button
              className="submit-job-button"
              onClick={() => navigate('/admin/training/submit')}
            >
              ➕ Submit New Job
            </button>
          )}
        </div>

      {/* Error Alert */}
      {error && (
        <div className="error-alert">
          <span className="error-icon">⚠️</span>
          <span>{error}</span>
          <button onClick={() => setError('')}>×</button>
        </div>
      )}

      {/* Filters */}
      <div className="filters-section">
        <div className="filter-group">
          <label>Domain</label>
          <select
            value={filters.domain}
            onChange={(e) => handleFilterChange('domain', e.target.value)}
          >
            <option value="">All Domains</option>
            <option value="WMS">WMS</option>
            <option value="iSeries">iSeries</option>
            <option value="BPCS">BPCS</option>
            <option value="ERPNext">ERPNext</option>
            <option value="Manufacturing">Manufacturing</option>
          </select>
        </div>

        <div className="filter-group">
          <label>Status</label>
          <select
            value={filters.status}
            onChange={(e) => handleFilterChange('status', e.target.value)}
          >
            <option value="">All Status</option>
            <option value="pending">Pending</option>
            <option value="running">Running</option>
            <option value="completed">Completed</option>
            <option value="failed">Failed</option>
            <option value="cancelled">Cancelled</option>
          </select>
        </div>

        <div className="filter-group">
          <label>Per Page</label>
          <select
            value={filters.limit}
            onChange={(e) => handleFilterChange('limit', parseInt(e.target.value))}
          >
            <option value="10">10</option>
            <option value="20">20</option>
            <option value="50">50</option>
            <option value="100">100</option>
          </select>
        </div>

        <button className="clear-filters" onClick={() => setFilters({
          domain: '',
          status: '',
          page: 1,
          limit: 20
        })}>
          Clear Filters
        </button>
      </div>

      {/* Stats */}
      <div className="jobs-stats">
        <div className="stat-item">
          <span className="stat-label">Total Jobs:</span>
          <span className="stat-value">{pagination.total}</span>
        </div>
        <div className="stat-item">
          <span className="stat-label">Showing:</span>
          <span className="stat-value">
            {jobs.length} of {pagination.total}
          </span>
        </div>
      </div>

      {/* Jobs Table */}
      {loading ? (
        <div className="loading-container">
          <div className="spinner-large"></div>
          <p>Loading training jobs...</p>
        </div>
      ) : jobs.length === 0 ? (
        <div className="empty-state">
          <div className="empty-icon">📊</div>
          <h3>No Training Jobs Found</h3>
          <p>
            {filters.domain || filters.status
              ? 'Try adjusting your filters'
              : 'Submit your first training job to get started'}
          </p>
          {hasPermission('training.create') && !filters.domain && !filters.status && (
            <button
              className="primary-button"
              onClick={() => navigate('/admin/training/submit')}
            >
              Submit Training Job
            </button>
          )}
        </div>
      ) : (
        <div className="jobs-table-container">
          <table className="jobs-table">
            <thead>
              <tr>
                <th>Job ID</th>
                <th>Domain</th>
                <th>Status</th>
                <th>Progress</th>
                <th>Submitted</th>
                <th>Duration</th>
                <th>Submitted By</th>
                <th>Actions</th>
              </tr>
            </thead>
            <tbody>
              {jobs.map(job => (
                <tr key={job._id}>
                  <td className="job-id-cell">
                    <code>{job.jobId || job._id.slice(-8)}</code>
                  </td>

                  <td className="job-domain-cell">
                    <span className="domain-badge">{job.domain}</span>
                  </td>

                  <td>
                    <span className={`status-badge ${getStatusClass(job.status)}`}>
                      {job.status}
                    </span>
                  </td>

                  <td className="progress-cell">
                    {job.status === 'running' && job.progress ? (
                      <div className="progress-bar">
                        <div
                          className="progress-fill"
                          style={{ width: `${job.progress.percentage || 0}%` }}
                        ></div>
                        <span className="progress-text">
                          {job.progress.percentage || 0}%
                        </span>
                      </div>
                    ) : (
                      <span className="no-progress">--</span>
                    )}
                  </td>

                  <td className="date-cell">{formatDate(job.createdAt)}</td>

                  <td className="duration-cell">{formatDuration(job)}</td>

                  <td className="submitter-cell">
                    {job.submittedBy?.username || '--'}
                  </td>

                  <td className="actions-cell">
                    <div className="action-buttons">
                      <button
                        className="action-btn view-btn"
                        onClick={() => navigate(`/admin/training/jobs/${job.jobId}`)}
                        title="View Details"
                      >
                        👁️
                      </button>

                      {job.status === 'failed' && hasPermission('training.retry') && (
                        <button
                          className="action-btn retry-btn"
                          onClick={() => handleRetry(job.jobId)}
                          title="Retry Job"
                        >
                          🔄
                        </button>
                      )}

                      {(job.status === 'pending' || job.status === 'running') &&
                        hasPermission('training.cancel') && (
                          <button
                            className="action-btn cancel-btn"
                            onClick={() => handleCancel(job.jobId)}
                            title="Cancel Job"
                          >
                            ❌
                          </button>
                        )}
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {/* Pagination */}
      {pagination.pages > 1 && (
        <div className="pagination">
          <button
            className="page-button"
            disabled={pagination.currentPage === 1}
            onClick={() => handlePageChange(pagination.currentPage - 1)}
          >
            ← Previous
          </button>

          <div className="page-numbers">
            {Array.from({ length: pagination.pages }, (_, i) => i + 1).map(page => (
              <button
                key={page}
                className={`page-number ${page === pagination.currentPage ? 'active' : ''}`}
                onClick={() => handlePageChange(page)}
              >
                {page}
              </button>
            ))}
          </div>

          <button
            className="page-button"
            disabled={pagination.currentPage === pagination.pages}
            onClick={() => handlePageChange(pagination.currentPage + 1)}
          >
            Next →
          </button>
        </div>
      )}
    </div>
    </AdminLayout>
  );
};

export default TrainingJobs;
